import React, { useRef, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";

// local component imports
import CustomImage from "../../../../../components/atoms/customImage";
import VideoPlayer from "../../../../../components/atoms/videoPlayer";
import GridLottieComponent from "../../../../../components/atoms/gridLottie";

// services and utils imports
import { getCellAccessibleLabel } from "../../../../../utils/dynamicHomeUtils/getCellAccessibleLabel";
import { useWidgetClick } from "../../../../../utils/dynamicHomeUtils/WidgetClickContext";
import { runOnNavigationIntent } from "../../../../../utils/common/navigationIntent";
import { setIsNavigatingAction } from "../../../../../store/slices/commonSlice";

/**
 * Renders a single media cell (image / video / lottie).
 * @param {object} props - Component props
 * @param {object} props.cell - Cell data including mediaUrl, imageUrl, linkUrl, title, alt
 * @param {number} props.index - Index of this cell in the carousel
 * @param {number} props.borderRadius - Border radius in pixels
 * @param {number} props.aspectRatio - Aspect ratio value (e.g. 1.5)
 * @param {Set} props.loadedVideos - Set of indices whose video has loaded
 * @param {object} props.videoRefs - Ref map to video DOM elements
 * @param {Function} props.onToggleVideoPlay - Callback to toggle video play/pause by index
 * @param {Function} props.setLoadedVideos - State setter for loadedVideos
 * @param {Function} props.setPlayingVideos - State setter for playingVideos, kept in sync with the element's own play/pause events
 * @param {boolean} props.isEager - Whether this above-fold image should avoid lazy loading
 * @param {boolean} props.isPriority - Whether this image should use fetchPriority="high" (first 3 above-fold widgets)
 * @returns {React.ReactElement} The rendered media cell
 */
const MediaCell = ({
  cell,
  index,
  borderRadius,
  borderThickness,
  borderColor,
  aspectRatio,
  loadedVideos,
  videoRefs,
  onToggleVideoPlay,
  setLoadedVideos,
  setPlayingVideos,
  isEager = false,
  isPriority = false,
}) => {
  const dispatch = useDispatch();
  const { onCellClick } = useWidgetClick() ?? {};
  const mediaSrc = cell.imageUrl || cell.mediaUrl;
  const mediaType =
    cell.mediaType === "image" ? (cell.mediaUrl?.includes(".mp4") ? "video" : "image") : cell.mediaType || "image";
  const accessibleLabel = getCellAccessibleLabel(cell);
  const isLinkedImage = Boolean(cell.linkUrl) && mediaType === "image";
  const localVideoRef = useRef(null);

  /** Reveals the video if it is already playing or has enough data buffered.
   * Covers the case where the video was already cached (e.g. reloading directly onto
   * this widget's viewport) and starts playing before React attaches its "playing"
   * listener — a race where both the loadeddata and playing events can be missed.
   */
  const revealIfPlayable = () => {
    const video = localVideoRef.current;
    if (video && (!video.paused || video.readyState >= 3)) {
      setLoadedVideos((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
    }
  };

  /** Registers the video DOM element ref for this cell.
   * @param {HTMLVideoElement|null} el - The video element.
   */
  const videoRefCallback = (el) => {
    localVideoRef.current = el;
    if (el) {
      videoRefs.current.set(index, el);
      revealIfPlayable();
    }
  };

  /** Explicitly trigger play after hydration since autoplay attribute alone
   * is not reliable on initial page load in React SSR environments.
   */
  useEffect(() => {
    if (mediaType === "video" && localVideoRef.current) {
      localVideoRef.current.play().catch(() => {});
      revealIfPlayable();
      const pollId = setInterval(revealIfPlayable, 150);
      const stopId = setTimeout(() => clearInterval(pollId), 2000);
      return () => {
        clearInterval(pollId);
        clearTimeout(stopId);
      };
    }
  }, [mediaType]);

  /** Marks this cell's video as fully loaded and explicitly plays it. */
  const onVideoLoadedData = () => {
    setLoadedVideos((prev) => new Set(prev).add(index));
    if (localVideoRef.current) {
      localVideoRef.current.play().catch(() => {});
    }
  };

  /** Toggles play/pause for this cell's video. */
  const onVideoClick = () => onToggleVideoPlay(index);

  /** Keeps the play/pause state in sync with the element's real state (autoplay, stalls, ended).
   * Also reveals the video on its first "playing" event — some browsers never fire
   * loadeddata for a video whose src carries a #t= media fragment, so loadeddata alone
   * can leave the cell stuck on the poster even while the video is actually playing.
   * @param {React.SyntheticEvent} _event - The play/pause event.
   * @param {boolean} playing - Whether the video is now playing.
   */
  const onVideoPlaying = (_event, playing) => {
    if (playing) setLoadedVideos((prev) => (prev.has(index) ? prev : new Set(prev).add(index)));
    setPlayingVideos?.((prev) => {
      if (prev.has(index) === playing) return prev;
      const next = new Set(prev);
      if (playing) next.add(index);
      else next.delete(index);
      return next;
    });
  };

  const mediaElement =
    mediaType === "lottie" ? (
      <div className="relative h-full w-full">
        <GridLottieComponent animationPath={cell.mediaUrl || cell.imageUrl} isLoop={true} />
      </div>
    ) : mediaType === "video" ? (
      <div className="relative h-full w-full">
        {!loadedVideos.has(index) && cell.imageUrl && (
          <div className="absolute inset-0 z-10">
            <img
              src={cell.imageUrl}
              alt={accessibleLabel}
              className="h-full w-full object-contain"
              loading={isEager || isPriority ? "eager" : "lazy"}
              fetchPriority={isPriority ? "high" : undefined}
            />
          </div>
        )}
        <VideoPlayer
          customRef={videoRefCallback}
          videoUrl={mediaSrc}
          title={cell.title || `carousel-cell-video-${index}`}
          classes={`h-full w-full object-cover transition-opacity duration-300 ${loadedVideos.has(index) ? "opacity-100" : "opacity-0"}`}
          isMuted={true}
          isLoop={true}
          isPlaysInline={true}
          isAutoPlay={true}
          preload="metadata"
          poster={cell.imageUrl}
          onLoadedData={onVideoLoadedData}
          onPlaying={onVideoPlaying}
          onClick={onVideoClick}
        />
      </div>
    ) : (
      <div className="relative h-full w-full">
        <CustomImage
          src={mediaSrc}
          alt={accessibleLabel}
          className="h-full w-full object-cover"
          width={100}
          height={100}
          isPriority={isPriority}
          loading={isEager || isPriority ? "eager" : "lazy"}
          fetchPriority={isPriority ? "high" : undefined}
        />
      </div>
    );

  /**
   * Fires Widget Clicked event when the cell link is clicked.
   * The navigation flag is gated so a slick drag-to-scroll — which ends in a click that
   * slick itself cancels — doesn't leave the navigation skeleton on screen.
   * @param {React.MouseEvent} event - Click event from the cell anchor.
   * @returns {void}
   */
  const onLinkClick = (event) => {
    runOnNavigationIntent(event, () => dispatch(setIsNavigatingAction(true)));
    onCellClick?.({
      action: mediaType === "video" ? "Video Clicked" : "Image Clicked",
      item: {
        title: cell.title,
        imageUrl: cell.imageUrl,
        videoUrl: mediaType === "video" ? mediaSrc : undefined,
        linkUrl: cell.linkUrl,
        position: index,
        item_position: index,
        item_row: 1,
        item_column: index + 1,
      },
    });
  };

  return (
    <div
      className="relative overflow-hidden"
      style={{
        aspectRatio: `${aspectRatio}`,
        borderRadius: `${borderRadius}px`,
        border: borderThickness ? `${borderThickness}px solid ${borderColor}` : undefined,
      }}
    >
      {cell.linkUrl ? (
        <a
          href={cell.linkUrl}
          className="block h-full w-full"
          aria-label={isLinkedImage ? undefined : accessibleLabel}
          onClick={onLinkClick}
        >
          {mediaElement}
        </a>
      ) : (
        mediaElement
      )}
    </div>
  );
};

MediaCell.propTypes = {
  cell: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  borderRadius: PropTypes.number,
  borderThickness: PropTypes.number,
  borderColor: PropTypes.string,
  aspectRatio: PropTypes.number,
  loadedVideos: PropTypes.instanceOf(Set).isRequired,
  videoRefs: PropTypes.object.isRequired,
  onToggleVideoPlay: PropTypes.func.isRequired,
  setLoadedVideos: PropTypes.func.isRequired,
  setPlayingVideos: PropTypes.func,
  isEager: PropTypes.bool,
  isPriority: PropTypes.bool,
};

export default MediaCell;
